<script setup>
import { computed, ref, onMounted, onUnmounted, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useConfig } from 'dashboard/composables/useConfig';
import KanbanItem from './KanbanItem.vue';
import KanbanFilter from './KanbanFilter.vue';
import Modal from '../../../../components/Modal.vue';
import StageColorPicker from './StageColorPicker.vue';
import FunnelAPI from '../../../../api/funnel';
import { emitter } from 'shared/helpers/mitt';

// Helper function to get initial filters synchronously
const getInitialColumnFilters = (columnId) => {
    const key = `kanban_column_${columnId}_applied_filters`;
    const storedFilters = localStorage.getItem(key);
    console.log('[DEBUG] getInitialColumnFilters - columnId:', columnId, 'tem filtros armazenados?', !!storedFilters);
    
    if (storedFilters) {
        try {
            // Basic validation: Ensure it looks like our filter object
            const parsed = JSON.parse(storedFilters);
            console.log('[DEBUG] Filtros carregados do localStorage:', parsed);
            
            // Garantir que agent_id seja número se existir
            if (parsed && parsed.agent_id !== null && parsed.agent_id !== undefined) {
                parsed.agent_id = Number(parsed.agent_id);
                console.log('[DEBUG] agent_id convertido para número:', parsed.agent_id, 'tipo:', typeof parsed.agent_id);
            }
            
            if (typeof parsed === 'object' && parsed !== null) {
                // Add more specific checks if needed (e.g., hasOwnProperty('priority'))
                 return parsed;
            }
            console.warn(`Invalid stored filter format for column ${columnId}. Clearing.`);
            localStorage.removeItem(key);
            return null;
        } catch (e) {
            console.error(`Error parsing stored filters for column ${columnId}:`, e);
            localStorage.removeItem(key); // Clean up invalid data
            return null;
        }
    }
    return null;
};

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  title: {
    type: String,
    required: true,
  },
  color: {
    type: String,
    default: 'orange',
  },
  items: {
    type: Array,
    required: true,
  },
  totalColumns: {
    type: Number,
    default: 0,
  },
  description: {
    type: String,
    default: '',
  },
});

const emit = defineEmits([
  'add',
  'edit',
  'delete',
  'drop',
  'itemClick',
  'itemsUpdated',
]);
const { t } = useI18n();

const store = useStore();
const { isStacklab } = useConfig();

// Adicionar estado para filtros
const showFilterModal = ref(false);
// Initialize columnFilters using the helper function
const columnFilters = ref(getInitialColumnFilters(props.id));
const showOptionsMenu = ref(false);
const columnSettings = ref({
  showWon: true,
  showLost: true,
  showAll: true,
  hideColumn: false,
});

const showEditStageModal = ref(false);
const editingStageForm = ref({
  name: '',
  color: '',
  description: '',
});

// Adicionar ref para controle do modal de ordenação
const showSortModal = ref(false);
const columnSort = ref({
  type: 'created_at', // default: data de criação
  direction: 'desc', // default: mais recentes primeiro
});

// Nova ref para controlar o estado de colapso em massa
const allItemsCollapsed = ref(false);

// Refs para Lazy Loading
const itemsToShowCount = ref(6); // Mostrar 6 itens inicialmente
const itemsIncrement = 10; // Carregar mais 10 a cada vez
const scrollContainer = ref(null); // Ref para o container de scroll

// Opções de ordenação disponíveis
const sortOptions = [
  { id: 'created_at', label: 'Data de Criação' },
  { id: 'title', label: 'Título' },
  { id: 'priority', label: 'Prioridade' },
  { id: 'value', label: 'Valor' },
];

// Storage key is now only needed for *saving* in handleFilterApply
const APPLIED_FILTER_STORAGE_KEY = computed(() => `kanban_column_${props.id}_applied_filters`);

// Carregar configurações do localStorage
onMounted(() => {
  // Load non-filter settings
  const savedSettings = localStorage.getItem(
    `kanban_column_${props.id}_settings`
  );
  if (savedSettings) {
    columnSettings.value = JSON.parse(savedSettings);
  }
  const savedSort = localStorage.getItem(`kanban_column_${props.id}_sort`);
  if (savedSort) {
    columnSort.value = JSON.parse(savedSort);
  }

  // Adicionar listener de scroll para lazy loading
  if (scrollContainer.value) {
    scrollContainer.value.addEventListener('scroll', handleScroll);
    // Verificar se precisa carregar mais itens logo no início
    nextTick(() => {
      if (
        scrollContainer.value &&
        scrollContainer.value.scrollHeight <= scrollContainer.value.clientHeight
      ) {
        loadMoreItems();
      }
    });
  }

  // Fechar menu ao clicar fora
  document.addEventListener('click', closeOptionsMenuOnClickOutside);
});

// Salvar configurações no localStorage
const saveColumnSettings = () => {
  localStorage.setItem(
    `kanban_column_${props.id}_settings`,
    JSON.stringify(columnSettings.value)
  );
};

// Salvar configurações de ordenação
const saveSortSettings = () => {
  localStorage.setItem(
    `kanban_column_${props.id}_sort`,
    JSON.stringify(columnSort.value)
  );
};

const filteredItems = computed(() => {
  if (columnSettings.value.hideColumn) {
    return [];
  }

  // Debug log para verificar o estado atual dos filtros
  if (columnFilters.value) {
    console.log('[DEBUG] filteredItems - usando filtros:', JSON.stringify(columnFilters.value));
    if (columnFilters.value.agent_id) {
      console.log('[DEBUG] filteredItems - agent_id:', columnFilters.value.agent_id, 'tipo:', typeof columnFilters.value.agent_id);
    }
  }

  // 1. Start with the items passed from the parent (already filtered by global search)
  let items = [...props.items];

  // 2. Apply local column filters (priority, value, agent, date)
  if (columnFilters.value) {
    items = items.filter(item => {
      // Filter by priority
      if (
        columnFilters.value.priority &&
        columnFilters.value.priority.length > 0
      ) {
        const itemPriority = item.item_details?.priority || 'none'; // Default to 'none' if undefined
        if (!columnFilters.value.priority.includes(itemPriority)) return false;
      }

      // Filter by value
      if (
        columnFilters.value.value &&
        (columnFilters.value.value.min !== undefined || columnFilters.value.value.max !== undefined)
      ) {
        const itemValue = parseFloat(item.item_details?.value) || 0;
        const min = columnFilters.value.value.min;
        const max = columnFilters.value.value.max;

        if (min !== undefined && min !== null && itemValue < min) return false;
        if (max !== undefined && max !== null && itemValue > max) return false;
      }

      // Filter by agent
      if (columnFilters.value.agent_id) {
        // Log para depuração
        console.log('[DEBUG] Filtrando por agente - columnFilter agent_id:', columnFilters.value.agent_id, 'tipo:', typeof columnFilters.value.agent_id);
        
        // Verificar várias possíveis propriedades onde o ID do agente pode estar armazenado
        const itemAgentId = item.assigned_agent_id || 
                           item.assignee_id || 
                           (item.item_details && item.item_details.agent_id) || 
                           (item.meta && item.meta.agent_id) || 
                           null;
                           
        console.log('[DEBUG] Item completo:', item);
        console.log('[DEBUG] Item agent_id encontrado:', itemAgentId, 'tipo:', typeof itemAgentId);
        
        // Caso especial: -1 significa "Não atribuído" (filtra por itens sem agente)
        if (columnFilters.value.agent_id === -1) {
          // Se o item TEM um agente atribuído, rejeitar
          if (itemAgentId !== null) {
            console.log('[DEBUG] Item rejeitado - tem agente atribuído, mas filtro é para "Não atribuído"');
            return false;
          }
          console.log('[DEBUG] Item aceito - sem agente atribuído');
          return true;
        }
        
        // Caso normal: filtrar por ID do agente
        if (itemAgentId != columnFilters.value.agent_id) {
          console.log('[DEBUG] Item rejeitado - não corresponde ao agente filtrado');
          return false;
        }
        
        console.log('[DEBUG] Item aceito - corresponde ao agente filtrado');
      }

      // Filter by date
      if (
        columnFilters.value.date &&
        (columnFilters.value.date.start || columnFilters.value.date.end)
      ) {
        const itemDate = new Date(item.created_at);
        if (isNaN(itemDate.getTime())) return false; // Skip invalid dates

        if (columnFilters.value.date.start) {
          const startDate = new Date(columnFilters.value.date.start);
          startDate.setHours(0, 0, 0, 0); // Compare from the beginning of the day
          if (itemDate < startDate) return false;
        }
        if (columnFilters.value.date.end) {
          const endDate = new Date(columnFilters.value.date.end);
          endDate.setHours(23, 59, 59, 999); // Compare until the end of the day
          if (itemDate > endDate) return false;
        }
      }

      return true; // Item passes all local filters
    });
  }

  // 3. Apply status filters (won/lost)
  items = items.filter(item => {
    const status = item.item_details?.status; // e.g., 'open', 'won', 'lost'
    // Keep items that are not 'won' or 'lost' (like 'open', 'pending', etc.)
    if (status !== 'won' && status !== 'lost') return true;
    // Apply toggles only to 'won' and 'lost' items
    if (status === 'won' && !columnSettings.value.showWon) return false;
    if (status === 'lost' && !columnSettings.value.showLost) return false;
    return true; // Item passes status filters
  });

  // 4. Apply sorting
  return items.sort((a, b) => {
    const direction = columnSort.value.direction === 'asc' ? 1 : -1;
    const type = columnSort.value.type;

    let valueA, valueB;

    switch (type) {
      case 'title':
        valueA = a.item_details?.title || '';
        valueB = b.item_details?.title || '';
        return direction * valueA.localeCompare(valueB);

      case 'priority': {
        const priorityOrder = {
          urgent: 4,
          high: 3,
          medium: 2,
          low: 1,
          none: 0, // Ensure 'none' or undefined priority is handled
        };
        valueA = priorityOrder[a.item_details?.priority || 'none'];
        valueB = priorityOrder[b.item_details?.priority || 'none'];
        return direction * (valueA - valueB);
      }

      case 'value':
        valueA = parseFloat(a.item_details?.value) || 0;
        valueB = parseFloat(b.item_details?.value) || 0;
        return direction * (valueA - valueB);

      case 'created_at': // Default sorting
      default:
        valueA = new Date(a.created_at);
        valueB = new Date(b.created_at);
        // Handle potential invalid dates during sorting
        if (isNaN(valueA.getTime())) return direction;
        if (isNaN(valueB.getTime())) return -direction;
        return direction * (valueA - valueB);
    }
  });
});

// Lista de itens a serem realmente exibidos com lazy loading
const displayedItems = computed(() => {
  return filteredItems.value.slice(0, itemsToShowCount.value);
});

// Resultados do filtro para exibir no modal
const filteredResults = computed(() => {
  if (!columnFilters.value) return { total: 0, stages: {} };

  const stages = {};
  stages[props.title] = filteredItems.value.length;

  return {
    total: filteredItems.value.length,
    stages,
  };
});

const columnTotal = computed(() => {
  return filteredItems.value.reduce((total, item) => {
    const value = parseFloat(item.item_details?.value) || 0;
    return total + value;
  }, 0);
});

const formattedTotal = computed(() => {
  if (!columnTotal.value) return '';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(columnTotal.value);
});

const darkenColor = color => {
  const hex = color.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  const darkenAmount = 0.85;
  const dr = Math.floor(r * darkenAmount);
  const dg = Math.floor(g * darkenAmount);
  const db = Math.floor(b * darkenAmount);

  return `#${dr.toString(16).padStart(2, '0')}${dg.toString(16).padStart(2, '0')}${db.toString(16).padStart(2, '0')}`;
};

const textColor = computed(() =>
  props.color ? darkenColor(props.color) : '#CC6E00'
);

const columnStyle = computed(() => ({
  backgroundColor: props.color ? `${props.color}40` : '#f8fafc',
}));

const countStyle = computed(() => ({
  backgroundColor: props.color ? `${props.color}15` : '#FFF4E5',
  color: textColor.value,
}));

const handleDrop = event => {
  const itemID = event.dataTransfer.getData('text/plain');
  console.log(
    '[COLUMN-DROP-DEBUG] Item arrastado ID:',
    itemID,
    'para coluna:',
    props.id
  );

  if (itemID) {
    const numericId = parseInt(itemID, 10);
    console.log(
      '[COLUMN-DROP-DEBUG] ID convertido para número:',
      numericId,
      typeof numericId
    );

    // Garantir que o ID seja passado como número para o componente pai
    emit('drop', {
      itemId: numericId,
      columnId: props.id,
    });

    console.log(
      '[COLUMN-DROP-DEBUG] Evento drop emitido para o componente pai'
    );
  }
};

const handleDragOver = event => {
  event.preventDefault();
};

const columnWidth = computed(() => {
  if (props.totalColumns <= 5) {
    return {
      minWidth: '320px',
      maxWidth: `calc((100% - ${(props.totalColumns - 1) * 1}rem) / ${props.totalColumns})`,
      width: '100%',
    };
  }
  return {
    minWidth: '350px',
    maxWidth: '350px',
    width: '350px',
  };
});

// Funções para o filtro de coluna
const handleFilter = () => {
  showFilterModal.value = true;
};

const handleFilterApply = (appliedFilters) => {
  console.log('[DEBUG] handleFilterApply recebeu filtros:', JSON.stringify(appliedFilters));
  if (appliedFilters && appliedFilters.agent_id) {
    console.log('[DEBUG] agent_id recebido:', appliedFilters.agent_id, 'tipo:', typeof appliedFilters.agent_id);
  }
  
  // Check if filters are effectively empty/cleared
   const isCleared = !appliedFilters ||
                     (
                       (!appliedFilters.priority || appliedFilters.priority.length === 0) &&
                       (!appliedFilters.value || (appliedFilters.value.min === null && appliedFilters.value.max === null)) &&
                       !appliedFilters.agent_id &&
                       (!appliedFilters.date || (!appliedFilters.date.start && !appliedFilters.date.end))
                     );

  if (isCleared) {
      console.log('[DEBUG] Filtros limpos');
      columnFilters.value = null; // Set local state to null
      localStorage.removeItem(APPLIED_FILTER_STORAGE_KEY.value); // Remove from storage
  } else {
      console.log('[DEBUG] Aplicando filtros, agent_id:', appliedFilters.agent_id, 'tipo:', typeof appliedFilters.agent_id);
      columnFilters.value = appliedFilters; // Update local state
      localStorage.setItem(APPLIED_FILTER_STORAGE_KEY.value, JSON.stringify(appliedFilters)); // Save to storage
      
      // Log para verificar o estado atual após aplicar
      console.log('[DEBUG] Filtros aplicados, agora columnFilters.agent_id:', 
          columnFilters.value?.agent_id, 
          'tipo:', typeof columnFilters.value?.agent_id);
  }
  showFilterModal.value = false;
  // Reset lazy load count on filter change
  itemsToShowCount.value = 6;
  nextTick(() => { handleScroll(); }); // Check if scroll needs adjustment
};

const hasActiveFilters = computed(() => {
  if (!columnFilters.value) return false;

  return (
    columnFilters.value.priority.length > 0 ||
    columnFilters.value.value.min ||
    columnFilters.value.value.max ||
    columnFilters.value.agent_id ||
    columnFilters.value.date.start ||
    columnFilters.value.date.end
  );
});

// Função para carregar mais itens (lazy loading)
const loadMoreItems = () => {
  if (itemsToShowCount.value < filteredItems.value.length) {
    itemsToShowCount.value = Math.min(
      itemsToShowCount.value + itemsIncrement,
      filteredItems.value.length
    );
  }
};

// Handler do evento de scroll para lazy loading
const handleScroll = () => {
  if (!scrollContainer.value) return;

  const { scrollTop, scrollHeight, clientHeight } = scrollContainer.value;
  const threshold = 150; // Carregar quando faltar 150px para o fim

  if (
    scrollTop + clientHeight >= scrollHeight - threshold &&
    itemsToShowCount.value < filteredItems.value.length // Evita chamadas desnecessárias
  ) {
    loadMoreItems();
  }
};

// Resetar a contagem de itens visíveis quando filtros/ordenação mudarem
watch(
  filteredItems,
  () => {
    itemsToShowCount.value = 6; // Resetar para 6 itens
    // Garantir que o scroll seja reavaliado após a atualização do DOM
    nextTick(() => {
      if (scrollContainer.value) {
        // Se o conteúdo inicial não preencher a área, carregar mais
        if (
          scrollContainer.value.scrollHeight <=
          scrollContainer.value.clientHeight
        ) {
          loadMoreItems();
        }
      }
    });
  },
  { deep: true } // Monitora mudanças profundas nos filtros/ordenação
);

// Fechar menu ao clicar fora
const closeOptionsMenuOnClickOutside = event => {
  // Lógica para verificar se o clique foi fora do botão e do menu
  // Esta função agora é nomeada para poder ser removida em onUnmounted
  // (Implementação simplificada, pode precisar de ajustes dependendo da estrutura exata do DOM)
  if (showOptionsMenu.value) {
     showOptionsMenu.value = false;
  }
};

onMounted(() => {
  document.addEventListener('click', closeOptionsMenuOnClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', closeOptionsMenuOnClickOutside);

  // Remover listener de scroll
  if (scrollContainer.value) {
    scrollContainer.value.removeEventListener('scroll', handleScroll);
  }
});

// Atualizar o watch para sincronizar as opções
watch(
  () => columnSettings.value.showAll,
  newValue => {
    if (newValue) {
      columnSettings.value.showWon = true;
      columnSettings.value.showLost = true;
    }
    saveColumnSettings();
  }
);

// Atualizar o watch para a opção "todos"
watch(
  [() => columnSettings.value.showWon, () => columnSettings.value.showLost],
  ([showWon, showLost]) => {
    columnSettings.value.showAll = showWon && showLost;
  }
);

const handleEditStage = () => {
  editingStageForm.value = {
    name: props.title,
    color: props.color,
    description: props.description || '',
  };
  showEditStageModal.value = true;
  showOptionsMenu.value = false;
};

const handleSaveStage = async () => {
  try {
    const funnel = store.getters['funnel/getSelectedFunnel'];
    const updatedStages = { ...funnel.stages };

    updatedStages[props.id] = {
      ...updatedStages[props.id],
      name: editingStageForm.value.name,
      color: editingStageForm.value.color,
      description: editingStageForm.value.description,
    };

    await FunnelAPI.update(funnel.id, {
      ...funnel,
      stages: updatedStages,
    });

    showEditStageModal.value = false;

    // Força atualização do store
    await store.dispatch('funnel/fetch');

    // Emite evento para atualizar o kanban
    emit('itemsUpdated');

    // Notifica sucesso
    emitter.emit('newToastMessage', {
      message: 'Etapa atualizada com sucesso',
      action: { type: 'success' },
    });
  } catch (error) {
    console.error('Erro ao atualizar etapa:', error);
    emitter.emit('newToastMessage', {
      message: 'Erro ao atualizar etapa',
      action: { type: 'error' },
    });
  }
};

// Nova função para colapsar ou expandir todos os itens da coluna
const toggleAllItems = () => {
  // Atualiza o estado local
  allItemsCollapsed.value = !allItemsCollapsed.value;

  // Carrega as informações de colapso existentes
  const collapsedItems = JSON.parse(
    localStorage.getItem('kanban_collapsed_items') || '{}'
  );

  // Atualiza o estado de todos os itens da coluna
  filteredItems.value.forEach(item => {
    collapsedItems[item.id] = allItemsCollapsed.value;
  });

  // Salva de volta no localStorage
  localStorage.setItem(
    'kanban_collapsed_items',
    JSON.stringify(collapsedItems)
  );

  // Fecha o menu de opções
  showOptionsMenu.value = false;

  // Força a atualização dos componentes KanbanItem
  // Emite evento especial para forçar os itens a recarregarem seu estado
  emit('itemsUpdated');

  // Dispara um evento global para notificar os itens
  emitter.emit('kanbanItemsCollapsedStateChanged');
};
</script>

<template>
  <div
    v-show="!columnSettings.hideColumn"
    class="flex flex-col rounded-lg overflow-hidden"
    :style="{ ...columnWidth, ...columnStyle }"
  >
    <div
      class="pl-4 pr-4 pt-2 pb-2"
      :style="{ borderBottom: `1px solid ${props.color}59` }"
    >
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2">
          <h3 class="text-sm font-medium" :style="{ color: textColor }">
            {{ title }}
          </h3>
          <span
            class="inline-flex items-center justify-center w-5 h-5 text-xs font-medium rounded-full"
            :style="countStyle"
          >
            {{ filteredItems.length }}
          </span>
          <!-- Indicador de filtro ativo -->
          <span
            v-if="hasActiveFilters"
            class="filter-active-indicator"
            :style="{ backgroundColor: textColor }"
          />
        </div>
        <div class="flex items-center gap-2">
          <span
            v-if="formattedTotal && isStacklab"
            class="text-xs font-medium"
            :style="{ color: textColor }"
          >
            {{ formattedTotal }}
          </span>
          <!-- Botão de filtro -->
          <button
            class="p-1 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100"
            @click="handleFilter"
          >
            <fluent-icon icon="filter" size="16" />
          </button>
          <button
            class="p-1 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100"
            @click="$emit('add', id)"
          >
            <fluent-icon icon="add" size="16" />
          </button>
          <!-- Botão de mais opções -->
          <div class="relative">
            <button
              class="p-1 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 options-menu-button"
              @click.stop="showOptionsMenu = !showOptionsMenu"
            >
              <fluent-icon icon="more-vertical" size="16" />
            </button>

            <!-- Menu de Opções -->
            <div
              v-if="showOptionsMenu"
              class="absolute right-0 top-full mt-1 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 py-2 w-48 z-10 options-menu-content"
              @click.stop
            >
              <div class="px-4 py-2">
                <button
                  class="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200 w-full hover:text-woot-500"
                  @click="handleEditStage"
                >
                  <fluent-icon icon="edit" size="16" />
                  <span>{{ t('KANBAN.COLUMN_OPTIONS.EDIT_STAGE') }}</span>
                </button>
              </div>
              <div
                class="border-t border-slate-100 dark:border-slate-700 my-1"
              ></div>
              <div class="px-4 py-2">
                <label
                  class="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200"
                >
                  <input
                    v-model="columnSettings.showAll"
                    type="checkbox"
                    class="form-checkbox"
                    @change="saveColumnSettings"
                  />
                  <span>{{ t('KANBAN.COLUMN_OPTIONS.SHOW_ALL') }}</span>
                </label>
              </div>
              <div class="px-4 py-2">
                <label
                  class="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200"
                >
                  <input
                    v-model="columnSettings.showWon"
                    type="checkbox"
                    class="form-checkbox"
                    @change="saveColumnSettings"
                  />
                  <span>{{ t('KANBAN.COLUMN_OPTIONS.SHOW_WON') }}</span>
                </label>
              </div>
              <div class="px-4 py-2">
                <label
                  class="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200"
                >
                  <input
                    v-model="columnSettings.showLost"
                    type="checkbox"
                    class="form-checkbox"
                    @change="saveColumnSettings"
                  />
                  <span>{{ t('KANBAN.COLUMN_OPTIONS.SHOW_LOST') }}</span>
                </label>
              </div>
              <div class="px-4 py-2">
                <label
                  class="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200"
                >
                  <input
                    v-model="columnSettings.hideColumn"
                    type="checkbox"
                    class="form-checkbox"
                    @change="saveColumnSettings"
                  />
                  <span>{{ t('KANBAN.COLUMN_OPTIONS.HIDE_STAGE') }}</span>
                </label>
              </div>
              <div class="px-4 py-2">
                <button
                  class="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200 w-full hover:text-woot-500"
                  @click="showSortModal = true"
                >
                  <fluent-icon icon="arrow-sort" size="16" />
                  <span>{{ t('KANBAN.COLUMN_OPTIONS.SORT') }}</span>
                </button>
              </div>
              <div
                class="border-t border-slate-100 dark:border-slate-700 my-1"
              ></div>
              <div class="px-4 py-2">
                <button
                  class="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200 w-full hover:text-woot-500"
                  @click="toggleAllItems"
                >
                  <fluent-icon
                    :icon="
                      allItemsCollapsed ? 'arrow-expand' : 'arrow-outwards'
                    "
                    size="16"
                  />
                  <span>{{ allItemsCollapsed ? t('KANBAN.COLUMN_OPTIONS.EXPAND_ALL') : t('KANBAN.COLUMN_OPTIONS.COLLAPSE_ALL') }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      ref="scrollContainer"
      class="flex-1 overflow-y-auto"
      @drop="handleDrop"
      @dragover="handleDragOver"
    >
      <div class="px-4">
        <div class="py-3 space-y-3">
          <template v-if="displayedItems.length">
            <KanbanItem
              v-for="item in displayedItems"
              :key="item.id"
              :item="item"
              :kanban-items="props.items"
              draggable
              @dragstart="
                e => {
                  e.dataTransfer.setData('text/plain', item.id.toString());
                }
              "
              @click="
                modifiedItem => {
                  console.log(
                    '[DEBUG] KanbanColumn - Item clicado:',
                    modifiedItem
                  );
                  console.log(
                    '[DEBUG] KanbanColumn - Description no clique:',
                    modifiedItem?.item_details?.description
                  );
                  $emit('itemClick', modifiedItem);
                }
              "
              @edit="
                modifiedItem => {
                  console.log(
                    '[DEBUG] KanbanColumn - Item editado:',
                    modifiedItem
                  );
                  console.log(
                    '[DEBUG] KanbanColumn - Description na edição:',
                    modifiedItem?.item_details?.description
                  );
                  $emit('edit', modifiedItem);
                }
              "
              @delete="$emit('delete', item)"
            />
          </template>
          <div
            v-else
            class="flex items-center justify-center h-24 text-sm text-slate-500 dark:text-slate-400"
          >
            {{
              hasActiveFilters ? t('KANBAN.NO_FILTERED_ITEMS') : t('KANBAN.NO_ITEMS')
            }}
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de filtro -->
    <KanbanFilter
      :show="showFilterModal"
      :filtered-results="filteredResults"
      :current-funnel="store.getters['funnel/getSelectedFunnel']"
      :initial-filters="columnFilters"
      size="large"
      @close="showFilterModal = false"
      @apply="handleFilterApply"
    />

    <!-- Modal de Edição de Etapa -->
    <Modal
      v-model:show="showEditStageModal"
      :on-close="() => (showEditStageModal = false)"
    >
      <div class="p-6">
        <h3 class="text-lg font-medium mb-4">{{ t('KANBAN.EDIT_STAGE_MODAL.TITLE') }}</h3>

        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium mb-1">{{ t('KANBAN.EDIT_STAGE_MODAL.NAME') }}</label>
            <input
              v-model="editingStageForm.name"
              type="text"
              class="w-full px-3 py-2 border rounded-lg"
              :placeholder="t('KANBAN.EDIT_STAGE_MODAL.NAME_PLACEHOLDER')"
            />
          </div>

          <div>
            <label class="block text-sm font-medium mb-1">{{ t('KANBAN.EDIT_STAGE_MODAL.COLOR') }}</label>
            <StageColorPicker v-model="editingStageForm.color" />
          </div>

          <div>
            <label class="block text-sm font-medium mb-1">{{ t('KANBAN.EDIT_STAGE_MODAL.DESCRIPTION') }}</label>
            <textarea
              v-model="editingStageForm.description"
              class="w-full px-3 py-2 border rounded-lg"
              rows="3"
              :placeholder="t('KANBAN.EDIT_STAGE_MODAL.DESCRIPTION_PLACEHOLDER')"
            />
          </div>

          <div class="flex justify-end gap-2">
            <woot-button variant="clear" @click="showEditStageModal = false">
              {{ t('KANBAN.FORM.CANCEL') }}
            </woot-button>
            <woot-button variant="primary" @click="handleSaveStage">
               {{ t('KANBAN.FORM.SAVE') }}
            </woot-button>
          </div>
        </div>
      </div>
    </Modal>

    <!-- Modal de Ordenação -->
    <Modal
      v-model:show="showSortModal"
      :on-close="() => (showSortModal = false)"
    >
      <div class="p-6">
        <h3 class="text-lg font-medium mb-4">{{ t('KANBAN.SORT_MODAL.TITLE') }}</h3>

        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium mb-2">{{ t('KANBAN.SORT_MODAL.SORT_BY') }}</label>
            <select
              v-model="columnSort.type"
              class="w-full px-3 py-2 border rounded-lg"
              @change="saveSortSettings"
            >
              <option
                v-for="option in sortOptions"
                :key="option.id"
                :value="option.id"
              >
                 {{ t(`KANBAN.SORT_OPTIONS.${option.id.toUpperCase()}`, option.label) }}
              </option>
            </select>
          </div>

          <div>
            <label class="block text-sm font-medium mb-2">{{ t('KANBAN.SORT_MODAL.DIRECTION') }}</label>
            <div class="flex gap-4">
              <label class="flex items-center gap-2">
                <input
                  type="radio"
                  v-model="columnSort.direction"
                  value="asc"
                  @change="saveSortSettings"
                />
                <span class="text-sm">{{ t('KANBAN.SORT_MODAL.ASCENDING') }}</span>
              </label>
              <label class="flex items-center gap-2">
                <input
                  type="radio"
                  v-model="columnSort.direction"
                  value="desc"
                  @change="saveSortSettings"
                />
                <span class="text-sm">{{ t('KANBAN.SORT_MODAL.DESCENDING') }}</span>
              </label>
            </div>
          </div>

          <div class="flex justify-end gap-2">
            <woot-button variant="clear" @click="showSortModal = false">
              {{ t('KANBAN.FORM.CANCEL') }}
            </woot-button>
            <woot-button variant="primary" @click="showSortModal = false">
              {{ t('KANBAN.FORM.APPLY') }}
            </woot-button>
          </div>
        </div>
      </div>
    </Modal>
  </div>
</template>

<style lang="scss" scoped>
.overflow-y-auto {
  min-height: 0;
  height: 100%;
  /* Adiciona um padding na parte inferior para dar espaço ao threshold do scroll */
  padding-bottom: 1px; /* Ajuste fino para garantir que o scrollHeight seja detectado corretamente */

  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.space-y-3 > :not([hidden]) ~ :not([hidden]) {
  margin-top: 0.75rem;
}

.space-y-3 > :last-child {
  margin-bottom: 0;
}

.filter-active-indicator {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  display: inline-block;
}

.form-checkbox {
  @apply rounded border-slate-300 text-woot-500 shadow-sm focus:border-woot-500 focus:ring focus:ring-woot-500 focus:ring-opacity-50;
}
</style>
